import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, isSameDay, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  CalendarDays, MapPin, User, FileText, Plus, Truck, ClipboardCheck, Layers, RefreshCw, Copy, Check, Calendar as CalendarIcon, ExternalLink, Smartphone, Globe
} from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// --- CONFIGURATION LOCALES ---
const locales = { 'fr': fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const messages = {
  allDay: 'Journée', previous: 'Précédent', next: 'Suivant', today: "Aujourd'hui",
  month: 'Mois', week: 'Semaine', day: 'Jour', agenda: 'Agenda',
  date: 'Date', time: 'Heure', event: 'Événement',
  noEventsInRange: 'Aucun événement', showMore: (total) => `+ ${total} autres`,
};

// --- PALETTE DE COULEURS DISTINCTES ---
const COLORS = {
  event: '#6366f1',           // Indigo (Événements génériques)
  quote_confirmed: '#10b981', // Emerald (Confirmé)
  quote_accepted: '#06b6d4',  // Cyan (Accepté - distinct du vert)
  quote_signed: '#f59e0b',    // Amber (Signé / En attente logistique)
  quote_sent: '#94a3b8',      // Slate (Envoyé / Brouillon)

  tour_ongoing: '#8b5cf6',    // Violet (Tournée active)
  tour_finished: '#475569',   // Gris sombre (Tournée passée/terminée)
  tour_group: '#d946ef',      // Fuchsia (Groupe de tournées)
};

// --- COMPOSANT : DÉTAIL D'UN ÉVÉNEMENT (OU GROUPE) ---
const EventDetailDialog = ({ event, open, onClose }) => {
  if (!event) return null;

  // Cas spécial : Groupe de tournées
  if (event.type === 'tour_group') {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Layers className="w-5 h-5 text-fuchsia-500" />
              {event.subEvents.length} Tournées ce jour
            </DialogTitle>
            <DialogDescription>{format(event.start, 'dd MMMM yyyy', { locale: fr })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {event.subEvents.map((sub, idx) => (
              <div key={idx} className="p-3 border rounded-lg bg-muted/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold flex items-center gap-2">
                    <Truck className="w-4 h-4" /> {sub.title}
                  </span>
                  <Badge variant="outline" className={sub.status === 'finished' ? "bg-slate-100" : "bg-violet-50 text-violet-700"}>
                    {sub.status === 'finished' ? 'Terminée' : 'En cours'}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div className="flex items-center gap-2"><User className="w-3 h-3" /> {sub.resource.driver_name || 'Sans chauffeur'}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {sub.resource.delivery_ids?.length || 0} Arrêts</div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Cas standard : Événement unique
  const isTour = event.type === 'tour';
  const isQuote = event.type === 'lead' || event.type === 'quote';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {isTour ? <Truck className="w-5 h-5" /> : isQuote ? <FileText className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
            {event.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Badge style={{ backgroundColor: event.color + '20', color: event.color, borderColor: event.color }} variant="outline">
            {event.status_label || event.status}
          </Badge>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarDays className="w-4 h-4" />
              <span>{format(new Date(event.start), 'dd/MM/yyyy')} {event.end && event.end !== event.start && ` - ${format(new Date(event.end), 'dd/MM/yyyy')}`}</span>
            </div>

            {(event.client_name || event.resource?.client_name) && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span>{event.client_name || event.resource.client_name}</span>
              </div>
            )}

            {isTour && event.resource?.driver_name && (
              <div className="flex items-center gap-2 text-muted-foreground"><User className="w-4 h-4" /> <span>Chauffeur : {event.resource.driver_name}</span></div>
            )}

            {event.location && (
              <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-4 h-4" /> <span>{event.location}</span></div>
            )}

            {isQuote && event.total_ttc && (
              <div className="pt-2 border-t"><p className="text-lg font-bold">{formatCurrency(event.total_ttc)}</p></div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- PAGE PRINCIPALE ---
const CalendarPage = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();

  // Filtres
  const [showLeads, setShowLeads] = useState(true);
  const [showTours, setShowTours] = useState(true);

  // États Calendrier
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState(Views.MONTH);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // États Synchro
  const [isSyncOpen, setIsSyncOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Fonction pour transformer l'URL http en webcal (pour Apps natives)
const getWebcalUrl = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\//, 'webcal://');
};

// Fonction pour créer le lien d'ajout direct Google Calendar
const getGoogleCalendarUrl = (url) => {
  if (!url) return '';
  // Google a besoin d'une URL absolue (http/https). 
  // Attention: Google ne peut pas lire "localhost", il faudra une URL publique (ngrok ou prod)
  return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url)}`;
};
  // 1. Data Fetching
  const { data: calendarData = [], isLoading: loadingCalendar } = useQuery({
    queryKey: ['calendar'],
    queryFn: async () => (await fetch(`${API_URL}/calendar`, { headers: { Authorization: `Bearer ${token}` } })).json(),
  });

  const { data: tours = [], isLoading: loadingTours } = useQuery({
    queryKey: ['tours'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/tours`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // 2. Logic de Groupement et Coloring
  const events = useMemo(() => {
    let allEvents = [];

    // --- A. TRAITEMENT DES LEADS / DEVIS ---
    if (showLeads) {
      const leadEvents = calendarData.map((item) => {
        let color = COLORS.event;
        let label = item.status;

        // Mapping précis des statuts
        if (item.status === 'confirmed') { color = COLORS.quote_confirmed; label = "Confirmé"; }
        else if (item.status === 'accepted') { color = COLORS.quote_accepted; label = "Accepté"; }
        else if (item.status === 'signed') { color = COLORS.quote_signed; label = "Signé"; }
        else if (item.status === 'sent') { color = COLORS.quote_sent; label = "Envoyé"; }

        return {
          ...item,
          start: new Date(item.start),
          end: new Date(item.end),
          type: 'lead',
          color: color,
          status_label: label
        };
      });
      allEvents = [...allEvents, ...leadEvents];
    }

    // --- B. TRAITEMENT ET GROUPEMENT DES TOURNÉES ---
    if (showTours) {
      // 1. On prépare les tournées brutes avec leur statut "Terminé" ou "En cours"
      const rawTours = tours.map(t => {
        const tourDate = new Date(t.date);
        const isFinished = isBefore(tourDate, new Date()) && !isSameDay(tourDate, new Date()); // Passé = Terminé

        return {
          id: t.id || t._id,
          title: `${t.reference}`,
          start: tourDate,
          end: tourDate,
          type: 'tour',
          status: isFinished ? 'finished' : 'ongoing',
          color: isFinished ? COLORS.tour_finished : COLORS.tour_ongoing,
          resource: t,
          status_label: isFinished ? 'Tournée Terminée' : 'Tournée En cours'
        };
      });

      // 2. Logique de Groupement (Clustering par date)
      const toursByDate = {};
      rawTours.forEach(tour => {
        const dateKey = format(tour.start, 'yyyy-MM-dd');
        if (!toursByDate[dateKey]) toursByDate[dateKey] = [];
        toursByDate[dateKey].push(tour);
      });

      // 3. Création des événements finaux (Single ou Group)
      Object.values(toursByDate).forEach(group => {
        if (group.length > 1) {
          // Créer un groupe
          allEvents.push({
            id: `group-${group[0].start.getTime()}`,
            title: `📦 ${group.length} Tournées`,
            start: group[0].start,
            end: group[0].end,
            type: 'tour_group',
            color: COLORS.tour_group, // Couleur Fuchsia pour les groupes
            subEvents: group, // On stocke les enfants ici
            status_label: 'Groupe'
          });
        } else {
          // Ajouter l'unique tournée normalement
          allEvents.push(group[0]);
        }
      });
    }

    return allEvents;
  }, [calendarData, tours, showLeads, showTours]);

  // --- HANDLERS ---
  const handleSelectEvent = (event) => { setSelectedEvent(event); setDialogOpen(true); };
  const handleSelectSlot = useCallback(({ start, end }) => {
    setSelectedSlot({ start, end: end > start ? end : addDays(start, 1) });
    setCreateDialogOpen(true);
  }, []);
  const handleNavigate = useCallback((d) => setCalendarDate(d), []);
  const handleViewChange = useCallback((v) => setCalendarView(v), []);

  const toSafeISOString = (d) => {
    if (!d) return null; const safe = new Date(d); safe.setHours(12, 0, 0, 0); return safe.toISOString();
  };
  const handleCreateQuote = () => {
    if (selectedSlot) navigate(`/quotes?start=${toSafeISOString(selectedSlot.start)}&end=${toSafeISOString(selectedSlot.end)}`);
    setCreateDialogOpen(false); setSelectedSlot(null);
  };

  // HANDLER SYNCHRO
  const handleOpenSync = async () => {
    setIsSyncOpen(true);
    try {
      const res = await fetch(`${API_URL}/calendar/token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFeedUrl(data.feed_url);
    } catch (e) {
      toast.error("Impossible de récupérer le lien de synchronisation");
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  // --- STYLE CUSTOM ---
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color,
        borderLeft: `4px solid ${adjustColorBrightness(event.color, -20)}`, // Bordure légèrement plus foncée
        borderRadius: '4px',
        opacity: event.status === 'finished' ? 0.7 : 1, // Opacité réduite pour les tournées passées
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '12px',
        fontWeight: '500',
        padding: '2px 6px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
      },
    };
  };

  // Petit utilitaire pour assombrir la bordure
  function adjustColorBrightness(col, percent) {
    if (!col) return '#000';
    let num = parseInt(col.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      B = ((num >> 8) & 0x00FF) + amt,
      G = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + (B < 255 ? B < 1 ? 0 : B : 255) * 0x100 + (G < 255 ? G < 1 ? 0 : G : 255)).toString(16).slice(1);
  }

  if (loadingCalendar || loadingTours) return <div className="space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-[600px] w-full" /></div>;

  return (
    <div className="space-y-6 h-[calc(100vh-100px)] flex flex-col">
      {/* HEADER & FILTRES */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 shrink-0">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-heading">Planning</h1>
            <p className="text-muted-foreground">Vue consolidée Commercial & Logistique</p>
          </div>
          {/* BOUTON SYNCHRO */}
          <Button variant="outline" size="sm" onClick={handleOpenSync} className="gap-2 ml-4">
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Synchro</span>
          </Button>
        </div>

        {/* FILTRES ADAPTATIFS (LIGHT / DARK) */}
        <div className="flex flex-wrap items-center gap-4 p-2 rounded-xl shadow-sm border 
                        bg-white border-slate-200 
                        dark:bg-slate-900 dark:border-slate-800">

          {/* Switch Devis */}
          <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg border 
                            bg-slate-50 border-slate-200 
                            dark:bg-slate-800/50 dark:border-slate-700">
            <Switch
              id="filter-leads"
              checked={showLeads}
              onCheckedChange={setShowLeads}
              className="data-[state=checked]:bg-blue-600"
            />
            <Label
              htmlFor="filter-leads"
              className="flex items-center gap-2 cursor-pointer text-sm font-medium 
                             text-slate-700 dark:text-slate-100"
            >
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-sm" />
              Projets / Devis
            </Label>
          </div>

          {/* Switch Tournées */}
          <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg border 
                            bg-slate-50 border-slate-200 
                            dark:bg-slate-800/50 dark:border-slate-700">
            <Switch
              id="filter-tours"
              checked={showTours}
              onCheckedChange={setShowTours}
              className="data-[state=checked]:bg-violet-600"
            />
            <Label
              htmlFor="filter-tours"
              className="flex items-center gap-2 cursor-pointer text-sm font-medium 
                             text-slate-700 dark:text-slate-100"
            >
              <div className="w-2.5 h-2.5 rounded-full bg-violet-500 shadow-sm" />
              Tournées
            </Label>
          </div>
        </div>
      </div>

      {/* LÉGENDE DÉTAILLÉE */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-muted-foreground px-1">
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#10b981]" /> Confirmé</div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#06b6d4]" /> Accepté</div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#f59e0b]" /> Signé (Attente)</div>
        <span className="w-px h-3 bg-slate-300 mx-2"></span>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#8b5cf6]" /> Tournée En cours</div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#475569]" /> Tournée Terminée</div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-[#d946ef]" /> Groupe</div>
      </div>

      {/* CALENDRIER */}
      <Card className="flex-1 border-none shadow-md overflow-hidden flex flex-col">
        <div className="flex-1 calendar-wrapper" style={{ '--rbc-event-bg': 'var(--primary)' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            messages={messages}
            culture="fr"
            date={calendarDate}
            view={calendarView}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            eventPropGetter={eventStyleGetter}
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            defaultView={Views.MONTH}
            popup
          />
        </div>
      </Card>

      {/* DIALOG CRÉATION DEVIS (SLOT CLICK) */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Plus className="w-5 h-5" /> Créer un devis</DialogTitle>
            <DialogDescription>Nouvelle période sélectionnée</DialogDescription>
          </DialogHeader>
          {selectedSlot && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg text-sm text-center">
                {format(selectedSlot.start, 'dd MMM', { locale: fr })} → {format(selectedSlot.end, 'dd MMM', { locale: fr })}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => { setCreateDialogOpen(false); setSelectedSlot(null); }}>Annuler</Button>
                <Button className="flex-1" onClick={handleCreateQuote}><Plus className="w-4 h-4 mr-2" /> Créer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- NOUVEAU : DIALOGUE SYNCHRO --- */}
      <Dialog open={isSyncOpen} onOpenChange={setIsSyncOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Synchronisation Calendrier
            </DialogTitle>
            <DialogDescription>
              Ajoutez votre planning à votre agenda personnel pour tout voir en temps réel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            
            {/* OPTION 1 : BOUTONS D'ACTION RAPIDE */}
            <div className="grid grid-cols-1 gap-3">
              {/* Bouton pour Apps (iOS, Mac, Outlook) */}
              <Button 
                className="w-full h-12 text-base justify-between px-4 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => window.location.href = getWebcalUrl(feedUrl)}
              >
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  <span>S'abonner (iPhone / Outlook)</span>
                </div>
                <ExternalLink className="w-4 h-4 opacity-70" />
              </Button>

              {/* Bouton Spécial Google Agenda Web */}
              <Button 
                variant="outline"
                className="w-full h-12 text-base justify-between px-4"
                onClick={() => window.open(getGoogleCalendarUrl(feedUrl), '_blank')}
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <span>Ajouter à Google Agenda</span>
                </div>
                <ExternalLink className="w-4 h-4 opacity-70" />
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Ou manuellement</span>
              </div>
            </div>

            {/* OPTION 2 : COPIER LE LIEN (BACKUP) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Lien du flux iCal (.ics)</Label>
              <div className="flex gap-2">
                <Input value={feedUrl} readOnly className="font-mono text-xs bg-gray-50 text-white-700 dark:bg-gray-800 dark:text-black-300" />
                <Button size="icon" variant="secondary" onClick={copyToClipboard}>
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                ⚠️ Un fichier sera téléchargé et vous devrez l'ouvrir
              </p>
            </div>

          </div>
        </DialogContent>
      </Dialog>

      {/* DIALOG DÉTAIL EVENT */}
      <EventDetailDialog event={selectedEvent} open={dialogOpen} onClose={() => { setDialogOpen(false); setSelectedEvent(null); }} />

      {/* CSS OVERRIDES POUR LE MODE SOMBRE */}
      <style>{`
        .rbc-calendar { font-family: inherit; }
        .dark .rbc-calendar { color: rgb(var(--foreground)); }
        .dark .rbc-month-view, .dark .rbc-time-view, .dark .rbc-agenda-view, .dark .rbc-header { background-color: rgb(var(--card)); border-color: rgb(var(--border)); color: rgb(var(--foreground)); }
        .dark .rbc-off-range-bg { background-color: rgba(0,0,0,0.2); }
        .dark .rbc-today { background-color: rgba(59,130,246,0.1) !important; }
        .dark .rbc-day-bg + .rbc-day-bg { border-left: 1px solid rgb(var(--border)); }
        .rbc-event { padding: 1px 4px !important; line-height: 1.3; }
        /* Pour cacher les événements passés en mois si besoin, décommenter : */
        /* .rbc-event.rbc-event-past { opacity: 0.6; } */
      `}</style>
    </div>
  );
};

export default CalendarPage;